import React, { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '../../utils/dateUtils';
import {
  User,
  Users,
  Plus,
  Edit,
  Trash2,
  Key,
  Shield,
  UserCheck,
  UserX,
  Unlock,
  Clock,
  Filter,
  Moon,
  Sun,
  Smile
} from 'lucide-react';
import apiService from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import DataTable, { Table } from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import ActionButton from '../common/ActionButton';
import BaseModal from '../common/BaseModal';
import FormField from '../common/FormField';
import ErrorAlert from '../common/ErrorAlert';
import FormActions from '../common/FormActions';
import OverviewStats from '../common/OverviewStats';

const UsersTab = () => {
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { is_active: statusFilter === 'active' })
      };
      
      const response = await apiService.getUsers(params);

      // Handle both array response and object response
      if (Array.isArray(response)) {
        setUsers(response);
        setPagination(prev => ({
          ...prev,
          total: response.length,
          pages: 1
        }));
      } else {
        setUsers(response.users || []);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0,
          pages: response.pages || 1
        }));
      }
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
      setUsers([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  }, [searchTerm, roleFilter, statusFilter, pagination.page, pagination.per_page]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Listen for custom events from sidebar
  useEffect(() => {
    const handlePasswordChange = (event) => {
      const { userId, username } = event.detail;
      // Find user by id or username
      let user;
      if (users && userId) {
        user = users.find(u => u.id === userId);
      } else if (users && username) {
        user = users.find(u => u.username === username);
      }
      if (user) {
        setSelectedUser(user);
        setShowPasswordModal(true);
      }
    };

    const handleUserProfile = (event) => {
      const { userId, username } = event.detail;
      // Find user by id or username
      let user;
      if (users && userId) {
        user = users.find(u => u.id === userId);
      } else if (users && username) {
        user = users.find(u => u.username === username);
      }
      if (user) {
        setSelectedUser(user);
        setIsOwnProfile(true); // Always true when coming from "View Profile" in sidebar
        setShowEditModal(true);
      }
    };

    window.addEventListener('openPasswordChange', handlePasswordChange);
    window.addEventListener('openUserProfile', handleUserProfile);

    return () => {
      window.removeEventListener('openPasswordChange', handlePasswordChange);
      window.removeEventListener('openUserProfile', handleUserProfile);
    };
  }, [users]);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowCreateModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsOwnProfile(false); // False when editing from table
    setShowEditModal(true);
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await apiService.deleteUser(userId);
      loadUsers();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      await apiService.unlockUser(userId);
      loadUsers();
    } catch (err) {
      console.error('Error unlocking user:', err);
      setError('Failed to unlock user');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'Administrator':
        return <Shield className="w-4 h-4 text-red-500" />;
      case 'User':
        return <User className="w-4 h-4 text-blue-500" />;
      case 'Viewer':
        return <UserCheck className="w-4 h-4 text-green-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };


  const getStatusIcon = (user) => {
    if (user.locked_until && new Date(user.locked_until) > new Date()) {
      return <UserX className="w-4 h-4 text-red-500" />;
    }
    if (!user.is_active) {
      return <UserX className="w-4 h-4 text-orange-500" />;
    }
    return <UserCheck className="w-4 h-4 text-green-500" />;
  };

  const isUserLocked = (user) => {
    return user.locked_until && new Date(user.locked_until) > new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateTime(dateString);
  };

  const columns = [
    {
      header: 'User',
      accessor: 'username',
      render: (user) => (
        <div className="flex items-center">
          {getStatusIcon(user)}
          <div className="ml-3">
            <div className="text-sm font-medium text-gray-900">{user.username}</div>
            {user.full_name && (
              <div className="text-sm text-gray-500">{user.full_name}</div>
            )}
            {user.email && (
              <div className="text-xs text-gray-400">{user.email}</div>
            )}
          </div>
        </div>
      )
    },
    {
      header: 'Role',
      accessor: 'role',
      render: (user) => (
        <div className="flex items-center space-x-2">
          {getRoleIcon(user.role)}
          <StatusBadge status={user.role} variant={getStatusVariant(user.role)} size="sm" />
        </div>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (user) => (
        <div className="flex flex-col space-y-1">
          {isUserLocked(user) ? (
            <StatusBadge status="Locked" variant="error" size="xs" />
          ) : user.is_active ? (
            <StatusBadge status="Active" variant="success" size="xs" />
          ) : (
            <StatusBadge status="Inactive" variant="warning" size="xs" />
          )}
          {user.failed_login_attempts > 0 && (
            <span className="text-xs text-orange-500">
              {user.failed_login_attempts} failed attempts
            </span>
          )}
        </div>
      )
    },
    {
      header: 'Last Login',
      accessor: 'last_login',
      render: (user) => user.last_login ? (
        <div className="flex items-center space-x-1 text-sm text-gray-500">
          <Clock className="w-3 h-3" />
          <span>{formatDate(user.last_login)}</span>
        </div>
      ) : (
        <span className="text-gray-400 text-sm">Never</span>
      )
    },
    {
      header: 'Created',
      accessor: 'created_at',
      render: (user) => (
        <span className="text-sm text-gray-500">{formatDate(user.created_at)}</span>
      )
    },
    {
      header: 'Actions',
      accessor: 'actions',
      className: 'text-right',
      render: (user) => (
        <div className="flex justify-end space-x-1">
          <ActionButton
            icon={Edit}
            onClick={() => handleEditUser(user)}
            variant="secondary"
            size="xs"
            tooltip="Edit User"
          />
          <ActionButton
            icon={Key}
            onClick={() => handleChangePassword(user)}
            variant="secondary"
            size="xs"
            tooltip="Change Password"
          />
          {isUserLocked(user) && (
            <ActionButton
              icon={Unlock}
              onClick={() => handleUnlockUser(user.id)}
              variant="secondary"
              size="xs"
              tooltip="Unlock Account"
            />
          )}
          <ActionButton
            icon={Trash2}
            onClick={() => setDeleteConfirm(user)}
            variant="danger"
            size="xs"
            tooltip="Delete User"
          />
        </div>
      )
    }
  ];

  const getStatusVariant = (role) => {
    switch (role) {
      case 'Administrator': return 'error';
      case 'User': return 'primary';
      case 'Viewer': return 'success';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* User Overview Stats */}
      <OverviewStats 
        show={showStats}
        stats={[
          {
            key: 'total-users',
            label: 'Total Users',
            value: users?.length || 0,
            icon: Users,
            iconColor: 'text-blue-500'
          },
          {
            key: 'active-users',
            label: 'Active Users',
            value: users?.filter(u => u.status === 'active')?.length || 0,
            icon: UserCheck,
            iconColor: 'text-green-500'
          },
          {
            key: 'locked-users',
            label: 'Locked Users',
            value: users?.filter(u => u.status === 'locked')?.length || 0,
            icon: UserX,
            iconColor: 'text-red-500'
          },
          {
            key: 'admin-users',
            label: 'Administrators',
            value: users?.filter(u => u.role === 'Administrator')?.length || 0,
            icon: Shield,
            iconColor: 'text-purple-500'
          }
        ]}
      />

      <DataTable
        title="User Management"
        subtitle="Manage system users, roles, and permissions"
        icon={Users}
        loading={loading}
        error={error}
        onRefresh={loadUsers}
        actions={[
          {
            icon: Plus,
            label: 'Create User',
            onClick: handleCreateUser,
            variant: 'primary'
          }
        ]}
        showStats={showStats}
        onToggleStats={() => setShowStats(!showStats)}
        searchProps={{
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
          placeholder: 'Search users...'
        }}
        filters={[
          {
            type: 'select',
            icon: Filter,
            value: roleFilter,
            onChange: (e) => setRoleFilter(e.target.value),
            options: [
              { value: '', label: 'All Roles' },
              { value: 'Administrator', label: 'Administrator' },
              { value: 'User', label: 'User' },
              { value: 'Viewer', label: 'Viewer' }
            ]
          },
          {
            type: 'select',
            icon: UserCheck,
            value: statusFilter,
            onChange: (e) => setStatusFilter(e.target.value),
            options: [
              { value: '', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' }
            ]
          }
        ]}
      >
        <Table
          columns={columns}
          data={users || []}
          rowKey="id"
        />
        
        {(!users || users.length === 0) && !loading && (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">Users will appear here once they are created.</p>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} users
            </div>
            <div className="flex space-x-2">
              <ActionButton
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                variant="secondary"
                size="sm"
              >
                Previous
              </ActionButton>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {pagination.page} of {pagination.pages}
              </span>
              <ActionButton
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                variant="secondary"
                size="sm"
              >
                Next
              </ActionButton>
            </div>
          </div>
        )}
      </DataTable>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <BaseModal
          isOpen={true}
          onClose={() => setDeleteConfirm(null)}
          title="Delete User"
          icon={Trash2}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete user <strong>{deleteConfirm.username}</strong>? This action cannot be undone.
            </p>
            <FormActions
              onCancel={() => setDeleteConfirm(null)}
              onSubmit={() => handleDeleteUser(deleteConfirm.id)}
              submitText="Delete"
              submitVariant="danger"
            />
          </div>
        </BaseModal>
      )}

      {/* Create/Edit User Modal */}
      {(showCreateModal || showEditModal) && (
        <UserModal
          user={selectedUser}
          isOwnProfile={isOwnProfile}
          onClose={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedUser(null);
            setIsOwnProfile(false);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedUser(null);
            setIsOwnProfile(false);
            loadUsers();
          }}
        />
      )}

      {/* Password Change Modal */}
      {showPasswordModal && selectedUser && (
        <PasswordChangeModal
          user={selectedUser}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
          onSave={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

// User Modal Component
const UserModal = ({ user, isOwnProfile = false, onClose, onSave }) => {
  const { theme, setTheme, easterEggsEnabled, toggleEasterEggs } = useTheme();
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    full_name: user?.full_name || '',
    password: '',
    confirm_password: '',
    role: user?.role || 'User',
    is_active: user?.is_active ?? true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user && formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const userData = {
        username: formData.username,
        email: formData.email || null,
        full_name: formData.full_name || null
      };

      // Only include role and is_active if not editing own profile
      if (!isOwnProfile) {
        userData.role = formData.role;
        userData.is_active = formData.is_active;
      }

      if (!user) {
        // Creating new user
        userData.password = formData.password;
        await apiService.createUser(userData);
      } else {
        // Updating existing user
        await apiService.updateUser(user.id, userData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.message || `Failed to ${user ? 'update' : 'create'} user`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title={user ? 'Edit User' : 'Create User'}
      icon={user ? Edit : Plus}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {error && <ErrorAlert error={error} variant="error" />}

        <FormField
          label="Username"
          type="text"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          required
          disabled={!!user}
          helpText={user ? "Username cannot be changed after creation" : undefined}
        />

        <FormField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />

        <FormField
          label="Full Name"
          type="text"
          value={formData.full_name}
          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
        />

        {!user && (
          <>
            <FormField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={8}
              helpText="Must be 8+ characters with uppercase, lowercase, and number"
            />

            <FormField
              label="Confirm Password"
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              required
            />
          </>
        )}

        {/* Only show role and active status if not editing own profile */}
        {!isOwnProfile && (
          <>
            <FormField
              label="Role"
              type="select"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              options={[
                { value: 'Viewer', label: 'Viewer' },
                { value: 'User', label: 'User' },
                { value: 'Administrator', label: 'Administrator' }
              ]}
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Account is active
              </label>
            </div>
          </>
        )}

        {/* Personal Settings - Only show for own profile */}
        {isOwnProfile && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Personal Settings</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Customize your personal preferences</p>

            {/* Theme Setting */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center space-x-3">
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                ) : (
                  <Sun className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Theme</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Choose your preferred color scheme</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setTheme('light')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-colors text-sm ${
                    theme === 'light'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Light
                </button>
                <button
                  type="button"
                  onClick={() => setTheme('dark')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-colors text-sm ${
                    theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Dark
                </button>
              </div>
            </div>

            {/* Easter Eggs Setting */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center space-x-3">
                <Smile className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Easter Eggs</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Enable fun hidden features throughout the app</p>
                </div>
              </div>
              <button
                type="button"
                onClick={toggleEasterEggs}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  easterEggsEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    easterEggsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        <FormActions
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitText={user ? 'Update' : 'Create'}
          loading={loading}
          loadingText="Saving..."
        />
      </form>
    </BaseModal>
  );
};

// Password Change Modal Component
const PasswordChangeModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiService.changePassword(user.id, {
        current_password: formData.current_password,
        new_password: formData.new_password
      });

      onSave();
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title={`Change Password for ${user?.username || 'User'}`}
      icon={Key}
      iconBgColor="bg-green-100"
      iconColor="text-green-600"
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {error && <ErrorAlert error={error} variant="error" />}

        {/* Hidden username field for password managers and accessibility */}
        <input
          type="text"
          name="username"
          value={user?.username || ''}
          autoComplete="username"
          readOnly
          style={{ display: 'none' }}
          aria-hidden="true"
        />

        <FormField
          label="Current Password"
          type="password"
          value={formData.current_password}
          onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
          required
          autoComplete="current-password"
        />

        <FormField
          label="New Password"
          type="password"
          value={formData.new_password}
          onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
          required
          minLength={8}
          autoComplete="new-password"
          helpText="Must be 8+ characters with uppercase, lowercase, and number"
        />

        <FormField
          label="Confirm New Password"
          type="password"
          value={formData.confirm_password}
          onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
          required
          autoComplete="new-password"
        />

        <FormActions
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitText="Change Password"
          loading={loading}
          loadingText="Changing..."
        />
      </form>
    </BaseModal>
  );
};

export default UsersTab;